import React, { useContext, useEffect, useState } from "react";
import axios from "axios";

// component
import { useIntl } from "react-intl";
import ButtonMain from "../../../../../../../../common/components/button/ButtonMain";

// utils
import { StoreContext } from "../../../../../../../../store/StoreProvider";
import { uploadFileApi } from "./uploadHelper";
import ApiUrls, {
  hsnaFsUrl,
} from "../../../../../../../../api/project/ApiUrls";
import Act from "../../../../../../../../store/actions";
import { OCR_FOLDER_NAME, OCR_UPLOAD_STATUS } from "../../../Config";

// image
import importImg from "../../../../../../../../../images/icon_import/icon_import_light/import.svg";
import uploadingErrImg from "../../../../../../../../../images/ocr_image/ocr_file_upload_error.svg";
import MsgDialog from "../../../../../../../../common/components/dialog/MsgDialog";
import { isEmpty } from "../../../../../../../../common/utils";

const UploadTool = () => {
  const [state, dispatch] = useContext(StoreContext);
  const { user, digitalAI } = state;
  const { ocrImgList, uploadErrorMsg, uploadOcrStatus } = digitalAI;
  const { id, defaultMsg } = uploadErrorMsg;
  const [uploadErr, setUploadErr] = useState(false); // 圖片上傳失敗
  const [reimport, setReimport] = useState(false); // 清除資料，重新上傳

  const intl = useIntl();

  const fetchDataFromDB = async ({ userId, folderName }) => {
    const apiUrl = ApiUrls.getOcrImgList
      .replace("{limit}", -1)
      .replace("{offset}", 0)
      .replace("{filename}", folderName)
      .replace("{userId}", userId);

    await axios
      .get(apiUrl)
      .then((res) => {
        const { data } = res.data;

        const newData = data.map((item) => ({
          ...item,
          _imgUrl: `${hsnaFsUrl}/static/hsna/${item.userId}/${item.imgUrl}`,
          checked: false,
        }));

        dispatch({
          type: Act.DIGITAL_AI_SET_IMG_LIST,
          payload: newData,
        });

        dispatch({
          type: Act.SNA_SET_OCR_ORIGINAL_DATA,
          payload: data,
        });

        dispatch({
          type: Act.DIGITAL_AI_SET_TOTAL_PAGE,
          payload: newData.length,
        });

        // 結束loading
        dispatch({
          type: Act.DIGITAL_AI_SET_UPLOAD_OCR_FILES_STATUS,
          payload: false,
        });
        localStorage.setItem(OCR_UPLOAD_STATUS, JSON.stringify(false));
      })
      .catch((err) => console.error(`Failed to fetch data from DB: ${err}`));
  };

  const checkProcessingStatus = async ({ userId, folderName }) => {
    try {
      const response = await axios.post(ApiUrls.checkOcrFile, {
        username: userId,
      });
      if (response.status === 200) {
        await fetchDataFromDB({ userId, folderName });
      } else if (response.status === 201) {
        // 如果處理還未完成，則間隔五秒後做下一次檢查
        setTimeout(async () => {
          await checkProcessingStatus({ userId, folderName });
        }, 5 * 1000);
      } else {
        localStorage.setItem(OCR_UPLOAD_STATUS, JSON.stringify(false));

        dispatch({
          type: Act.DIGITAL_AI_SET_UPLOAD_OCR_FILES_STATUS,
          payload: false,
        });
      }
    } catch (error) {
      localStorage.setItem(OCR_UPLOAD_STATUS, JSON.stringify(false));

      dispatch({
        type: Act.DIGITAL_AI_SET_UPLOAD_OCR_FILES_STATUS,
        payload: false,
      });
    }
  };

  const handleUpload = () => {
    uploadFileApi({
      user,
      onUploadLoading: (status) => {
        dispatch({
          type: Act.DIGITAL_AI_SET_UPLOAD_OCR_FILES_STATUS,
          payload: status,
        });
        localStorage.setItem(OCR_UPLOAD_STATUS, JSON.stringify(status));
      },
      onSuccess: checkProcessingStatus,
      onError: ({ message }) => {
        dispatch({
          type: Act.DIGITAL_OCR_UPLOAD_ERROR,
          payload: message,
        });
        setUploadErr(true);
      },
    });
  };

  const handleOpen = () => {
    if (!isEmpty(ocrImgList)) {
      dispatch({
        type: Act.DIGITAL_OCR_UPLOAD_ERROR,
        payload: {
          id: "sna.sidetool.digitalAITool.ocr.clear",
          defaultMsg: `重新匯入需清除現有資料及OCR結果。請注意，清除資料後將無法復原。`,
        },
      });
      setReimport(true);
      return;
    }

    handleUpload();
  };

  useEffect(() => {
    const checkStatus = localStorage.getItem(OCR_UPLOAD_STATUS);

    if (checkStatus === "true") {
      const checkFolderName = localStorage.getItem(OCR_FOLDER_NAME);
      if (checkFolderName) {
        dispatch({
          type: Act.DIGITAL_AI_SET_UPLOAD_OCR_FILES_STATUS,
          payload: Boolean(checkStatus),
        });

        checkProcessingStatus({
          userId: user.uid,
          folderName: checkFolderName,
        });
      }
    } else {
      dispatch({
        type: Act.DIGITAL_AI_SET_UPLOAD_OCR_FILES_STATUS,
        payload: false,
      });
    }
  }, []);

  const handleClear = () => {
    dispatch({
      type: Act.DIGITAL_AI_INIT,
    });
    dispatch({
      type: Act.SNA_SET_OCR_ORIGINAL_DATA,
      payload: "",
    });
    handleUpload();
    setReimport(false);
  };

  const handleClose = () => {
    setReimport(false);
  };

  return (
    <>
      <ButtonMain
        buttonProps={{
          sx: { "&.MuiButton-root": { borderRadius: "8px" }, width: "170px" },
          text: intl.formatMessage({
            id: "sna.sidetool.digitalAITool.ocr.uploadBtn",
            defaultMessage: "File upload",
          }),
          fullWidth: true,
          onClick: handleOpen,
          variant: "linearGradient",
          disabled: uploadOcrStatus,
          startIcon: <img src={importImg} alt="importIcon" />,
        }}
      />
      {/* 上傳失敗提醒窗 */}
      <MsgDialog
        setOpen={setUploadErr}
        open={uploadErr}
        src={uploadingErrImg}
        alt="upload Error"
        dialogTitleText={intl.formatMessage({
          id,
          defaultMessage: defaultMsg,
        })}
      />
      {/* 清除資料，重新上傳對話框 */}
      <MsgDialog
        setOpen={setReimport}
        open={reimport}
        src={uploadingErrImg}
        alt="upload"
        dialogTitleText={intl.formatMessage({
          id: "sna.sidetool.digitalAITool.ocr.clear.title",
          defaultMessage: "重新匯入",
        })}
        dialogContentText={intl.formatMessage({
          id,
          defaultMessage: defaultMsg,
        })}
        dialogActionYes={intl.formatMessage({
          id: "sna.sidetool.digitalAITool.ocr.dialog.clear.confirm",
          defaultMessage: "確認",
        })}
        dialogActionNo={intl.formatMessage({
          id: "sna.sidetool.digitalAITool.ocr.dialog.cancel",
          defaultMessage: "取消",
        })}
        action
        handleConfirm={handleClear}
        handleCancel={handleClose}
      />
    </>
  );
};

export default UploadTool;
